import React, { useCallback, useEffect, useState } from "react";
import TreatmentPlanModal from "./TreatmentPlanModal";
import TreatmentPlanHistory from "./TreatmentPlanHistory";
import { Patient } from "../../PatientCard";
import TreatmentPlanService, {
  TreatmentPlan,
} from "../../../../services/treatmentPlanService";

interface PatientTreatmentPlansTabProps {
  patient: Patient;
}

const PatientTreatmentPlansTab: React.FC<PatientTreatmentPlansTabProps> = ({
  patient,
}) => {
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const calculateStats = () => {
    const activePlans = treatmentPlans.filter(
      (plan) => plan.status === "active"
    );
    const completedPlans = treatmentPlans.filter(
      (plan) => plan.status === "completed"
    );
    const totalPendingItems = treatmentPlans.reduce(
      (sum, plan) => sum + (plan.pending_items_count || 0),
      0
    );
    const totalCriticalItems = treatmentPlans.reduce(
      (sum, plan) => sum + (plan.critical_items_count || 0),
      0
    );

    // Calculate average progress
    const totalProgress = treatmentPlans.reduce(
      (sum, plan) => sum + plan.progress_percentage,
      0
    );
    const avgProgress =
      treatmentPlans.length > 0
        ? Math.round(totalProgress / treatmentPlans.length)
        : 0;

    // Find next review date (earliest end_date of active plans)
    const activePlansWithEndDate = activePlans.filter((plan) => plan.end_date);
    const nextReview =
      activePlansWithEndDate.length > 0
        ? activePlansWithEndDate.reduce(
            (earliest, plan) =>
              !earliest || new Date(plan.end_date!) < new Date(earliest)
                ? plan.end_date!
                : earliest,
            null as string | null
          )
        : null;

    return {
      activePlans: activePlans.length,
      completedPlans: completedPlans.length,
      pendingItems: totalPendingItems,
      avgProgress,
      criticalItems: totalCriticalItems,
      nextReview: nextReview
        ? TreatmentPlanService.formatDate(nextReview)
        : "--",
    };
  };

  const handlePlanCreated = (planId: number) => {
    // alert("Treatment plan created successfully!");
    setShowPlanModal(false);
    // Trigger refresh for both history and stats
    setRefreshTrigger((prev) => prev + 1);
    loadTreatmentPlans();
  };

  const containerStyle: React.CSSProperties = {
    padding: "16px",
    minHeight: "100vh",
    maxWidth: "100%",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const headerContentStyle: React.CSSProperties = {
    display: "flex",
    // flexDirection: "column",
    gap: "12px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: "12px 16px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#10b981" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minWidth: "140px",
    textAlign: "center",
  });

  const statsStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const loadTreatmentPlans = useCallback(async () => {
    if (!patient.id) return;

    setStatsLoading(true);
    try {
      const plans = await TreatmentPlanService.getPatientTreatmentPlans(
        patient.id
      );
      setTreatmentPlans(plans);
    } catch (error) {
      console.error("Failed to load treatment plans for stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [patient.id]);

  // Add useEffect to load data when component mounts
  useEffect(() => {
    loadTreatmentPlans();
  }, [loadTreatmentPlans]);

  return (
    <>
      <div style={containerStyle}>
        {/* Header with Action Buttons */}
        <div style={headerStyle} className="nn">
          <div
            className="nn"
            style={{
              ...headerContentStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  margin: 0,
                  marginBottom: "4px",
                  color: "#1f2937",
                }}
              >
                Treatment Plans
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: 0,
                  lineHeight: "1.4",
                }}
              >
                Create and manage treatment plans for{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <button
                style={buttonStyle("primary")}
                onClick={() => setShowPlanModal(true)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#059669")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#10b981")
                }
              >
                New Treatment Plan
              </button>
            </div>
          </div>
        </div>

        {/* Patient Medical Context */}
        {(patient.chronic_conditions?.length || patient.allergies?.length) && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #dbeafe",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <h4
              style={{
                color: "#1e40af",
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Medical Context for Treatment Planning
            </h4>
            {patient.chronic_conditions?.length && (
              <div
                style={{
                  color: "#1e40af",
                  fontSize: "13px",
                  marginBottom: "4px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Chronic Conditions:</strong>{" "}
                {patient.chronic_conditions.join(", ")}
              </div>
            )}
            {patient.allergies?.length && (
              <div
                style={{
                  color: "#1e40af",
                  fontSize: "13px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Allergies:</strong> {patient.allergies.join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Treatment Plan History - Inline */}
        <TreatmentPlanHistory
          patient={patient}
          isOpen={true}
          onClose={() => {}}
          refreshTrigger={refreshTrigger}
        />

        {/* Treatment Statistics */}
        <div style={statsStyle}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            Treatment Overview
          </h4>

          {statsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #10b981",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "16px",
              }}
            >
              {(() => {
                const stats = calculateStats();
                return (
                  <>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#10b981",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.activePlans}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Active Plans
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.completedPlans}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Completed Plans
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#f59e0b",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.pendingItems}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Pending Items
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#3b82f6",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.avgProgress}%
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Avg. Progress
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#ef4444",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.criticalItems}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Critical Items
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#8b5cf6",
                          marginBottom: "4px",
                          wordBreak: "break-word",
                        }}
                      >
                        {stats.nextReview}
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Next Review
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Information Panel */}
        {/* <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            padding: "16px",
            marginTop: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "20px", marginBottom: "8px" }}>🎯</div>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#166534",
            }}
          >
            Comprehensive Treatment Planning
          </h4>
          <p
            style={{
              fontSize: "14px",
              color: "#166534",
              marginBottom: "16px",
              lineHeight: "1.5",
            }}
          >
            Create detailed treatment plans with step-by-step items, progress
            tracking, and automated reminders. Use templates for common
            conditions or build custom plans tailored to each patient's needs.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "8px",
              fontSize: "12px",
              color: "#166534",
            }}
          >
            <div>✅ Custom Treatment Plans</div>
            <div>✅ Progress Tracking</div>
            <div>✅ Item Status Management</div>
            <div>✅ Template Library</div>
            <div>✅ Critical Item Alerts</div>
            <div>✅ Outcome Documentation</div>
          </div>
        </div> */}
      </div>

      {/* Treatment Plan Modal */}
      <TreatmentPlanModal
        patient={patient}
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onPlanCreated={handlePlanCreated}
      />

      <style>
        {`
         @keyframes spin {
           0% { transform: rotate(0deg); }
           100% { transform: rotate(360deg); }
         }
         
         @media (max-width: 768px) {
           .header-actions {
             flex-direction: column;
             align-items: stretch;
           }
           
           .stats-grid {
             grid-template-columns: repeat(2, 1fr);
           }
           
           .info-grid {
             grid-template-columns: 1fr;
           }
         }
         
         @media (max-width: 480px) {
           .stats-grid {
             grid-template-columns: 1fr;
           }
         }
       `}
      </style>
    </>
  );
};

export default PatientTreatmentPlansTab;
